
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { User, UserRole, Company, SubscriptionPlan, Lead } from './types';
import { api, AUTH_EXPIRED_EVENT } from './services/apiService';
import { MOCK_COMPANIES } from './constants';
import Sidebar from './components/Sidebar';
import NotificationCenter from './components/NotificationCenter';
import SuperAdminDashboard from './views/SuperAdminDashboard';
import CompanyDashboard from './views/CompanyDashboard';
import UserDashboard from './views/UserDashboard';
import SettingsView from './views/SettingsView';
import AnalyticsView from './views/AnalyticsView';
import Login from './views/Login';
import AppErrorBoundary from './components/AppErrorBoundary';
import { isDbMode, isDemoMode } from './services/runtimeMode';


const App: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  // Nouvel état pour stocker le Super Admin quand il prend la main sur un compte
  const [originalUser, setOriginalUser] = useState<User | null>(null);
  
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [view, setView] = useState<'dashboard' | 'leads' | 'tasks' | 'opportunities' | 'users' | 'calls' | 'billing' | 'planning' | 'settings' | 'analytics' | 'companies' | 'team' | 'audit'>('dashboard');
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoringSession, setIsRestoringSession] = useState(true);
  const [isDark, setIsDark] = useState<boolean>(() => localStorage.getItem('orion_theme') !== 'light');

  // Profile edit
  const [showProfileEdit, setShowProfileEdit] = useState(false);
  const [profileForm, setProfileForm] = useState({ email: '', password: '', confirmPassword: '', currentPassword: '' });
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [profileSaving, setProfileSaving] = useState(false);

  // Global search
  const [globalSearch, setGlobalSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Lead[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const searchRef = useRef<HTMLDivElement>(null);

  // Global search handler
  const handleGlobalSearch = useCallback((query: string) => {
    setGlobalSearch(query);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!query.trim() || query.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    searchTimeout.current = setTimeout(async () => {
      try {
        const companyId = currentCompany?.id;
        if (!companyId) return;
        const leads = await api.getLeads({ companyId, userId: currentUser?.id ?? '', role: currentUser?.role ?? '' });
        const q = query.toLowerCase();
        const filtered = leads.filter(l =>
          (l.name || '').toLowerCase().includes(q) ||
          (l.email || '').toLowerCase().includes(q) ||
          (l.phone || '').toLowerCase().includes(q) ||
          (l.clientCompanyName || '').toLowerCase().includes(q) ||
          (l.tag || '').toLowerCase().includes(q)
        ).slice(0, 8);
        setSearchResults(filtered);
        setShowSearchResults(true);
      } catch { /* ignore */ }
    }, 300);
  }, [currentCompany]);

  // Close search on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Restauration de session au démarrage : lit le token JWT dans localStorage
  // et appelle /api?action=me pour récupérer l'utilisateur sans demander
  // une reconnexion à chaque refresh de page.
  useEffect(() => {
    api.restoreSession().then(user => {
      if (user) {
        handleLogin(user).finally(() => setIsRestoringSession(false));
      } else {
        setIsRestoringSession(false);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isDark) {
      document.documentElement.classList.remove('light');
      document.body.style.background = '#070f22';
    } else {
      document.documentElement.classList.add('light');
      document.body.style.background = '#eef2ff';
    }
    localStorage.setItem('orion_theme', isDark ? 'dark' : 'light');
  }, [isDark]);

  useEffect(() => {
    // SUPPRESSION DE L'ÉCRAN DE CHARGEMENT DÈS QUE L'APP EST MONTÉE
    const fallback = document.getElementById('loading-fallback');
    if (fallback) fallback.style.display = 'none';
  }, []);

  useEffect(() => {
    const onAuthExpired = () => {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('orion_crm_refresh_token');
      setCurrentUser(null);
      setCurrentCompany(null);
      setOriginalUser(null);
    };

    window.addEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as EventListener);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onAuthExpired as EventListener);
  }, []);

  const handleLogin = async (user: User) => {
    setIsLoading(true);
    try {
      setCurrentUser(user);
      if (user.companyId !== 'system') {
        let companies = await api.getCompanies();
        if (isDemoMode && (!companies || companies.length === 0)) companies = MOCK_COMPANIES;
        const company = companies.find(c => String(c.id) === String(user.companyId));
        
        // Fallback robuste si la compagnie n'est pas trouvée (ex: création récente)
        setCurrentCompany(company || { 
          id: user.companyId, 
          name: user.name.includes("Admin") ? user.name.replace("Admin ", "") : "Nouvelle Entreprise", 
          plan: SubscriptionPlan.BASIC, 
          userLimit: 3, 
          activeLeads: 0, 
          status: 'active', 
          createdAt: new Date().toISOString(),
          currency: 'EUR'
        } as any);
      } else {
          setCurrentCompany(null);
      }
      setView('dashboard');
    } catch (err) {
      console.error("Login Error:", err);
      alert("Erreur de connexion. Veuillez réessayer.");
    } finally {
      setIsLoading(false);
    }
  };

  // Wrapper spécifique pour l'impersonation qui sauvegarde l'admin actuel
  const handleImpersonate = async (targetUser: User) => {
      setOriginalUser(currentUser); // On sauvegarde le Super Admin
      await handleLogin(targetUser); // On connecte le faux admin
  };

  // Fonction pour revenir au Super Admin
  const handleStopImpersonation = () => {
      if (originalUser) {
          setCurrentUser(originalUser);
          setCurrentCompany(null); // Retour vue système
          setOriginalUser(null);
          setView('dashboard');
      }
  };

  const handleLogout = () => {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('orion_crm_refresh_token');
    setCurrentUser(null);
    setCurrentCompany(null);
    setOriginalUser(null);
  };

  const isSystemUser = currentUser?.role === UserRole.SUPER_ADMIN || currentUser?.role === UserRole.SUPPORT_USER;

  useEffect(() => {
    if (!currentUser || !currentCompany || isSystemUser) return;

    const checkTaskReminders = async () => {
      try {
        await api.getTasks({ companyId: currentCompany.id, status: 'todo' });
        await api.checkUpcomingTaskReminders(currentUser.id);
      } catch (e) {
        console.warn('Task reminder check failed');
      }
    };

    checkTaskReminders();
    const interval = setInterval(checkTaskReminders, 60000);
    return () => clearInterval(interval);
  }, [currentUser, currentCompany, isSystemUser]);

  if (isRestoringSession) return (
    <div className="fixed inset-0 flex items-center justify-center bg-slate-950">
      <div className="flex flex-col items-center gap-4">
        <svg className="w-10 h-10 animate-spin text-indigo-500" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
        </svg>
        <span className="text-slate-400 text-sm font-semibold tracking-widest uppercase">Chargement...</span>
      </div>
    </div>
  );

  if (!currentUser) return <Login onLogin={handleLogin} />;

  const updateCompanyInState = (updatedCompany: Company) => {
    setCurrentCompany(updatedCompany);
  };

  return (
    <AppErrorBoundary>
    <div className="orion-suite-crm flex h-screen">
      <Sidebar
        role={currentUser.role}
        activeView={view}
        onViewChange={setView}
        onLogout={handleLogout}
        userName={currentUser.name}
        companyName={currentCompany?.name || (isSystemUser ? 'ORION Admin' : 'CRM')}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />
      <main className="orion-main-surface flex-1 min-w-0 p-3 md:p-5 flex flex-col overflow-hidden">

        {/* Barre de recherche mobile (plein écran) */}
        {mobileSearchOpen && (
          <div className="fixed inset-0 z-[60] bg-black/70 flex items-start pt-4 px-4 lg:hidden" onClick={() => setMobileSearchOpen(false)}>
            <div ref={searchRef} className="w-full" onClick={e => e.stopPropagation()}>
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
                <input
                  autoFocus
                  type="text"
                  value={globalSearch}
                  onChange={(e) => handleGlobalSearch(e.target.value)}
                  placeholder="Rechercher un prospect..."
                  className="w-full pl-9 pr-10 py-3 rounded-xl bg-slate-900 border border-slate-700 text-sm text-slate-200 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-cyan-500/30"
                />
                <button type="button" onClick={() => setMobileSearchOpen(false)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              {showSearchResults && searchResults.length > 0 && (
                <div className="mt-2 bg-slate-900 rounded-xl border border-slate-700 overflow-hidden shadow-2xl">
                  {searchResults.map(lead => (
                    <button key={lead.id} type="button"
                      className="w-full text-left px-4 py-3 hover:bg-slate-800 transition-colors flex items-center gap-3 border-b border-slate-800 last:border-0"
                      onClick={() => { setView('leads'); setGlobalSearch(''); setShowSearchResults(false); setMobileSearchOpen(false); }}>
                      <div className="w-8 h-8 rounded-lg bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-black text-indigo-400">{(lead.name || '?')[0].toUpperCase()}</span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-bold text-white truncate">{lead.name}</p>
                        <p className="text-[10px] text-slate-400 truncate">{lead.clientCompanyName || lead.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <header className="orion-shell-header mb-3 flex items-center justify-between gap-2 flex-wrap flex-shrink-0">
          {/* Gauche : hamburger (mobile) + titre */}
          <div className="flex items-center gap-3 min-w-0">
            {/* Hamburger mobile */}
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition-all flex-shrink-0"
              aria-label="Ouvrir le menu"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <div className="min-w-0">
              <h1 className="text-xl md:text-3xl font-bold capitalize text-slate-100 tracking-tight truncate">
                {view === 'settings' ? 'Paramètres' : view === 'analytics' ? 'Performance' : view === 'leads' ? 'Prospects' : view === 'tasks' ? 'Tâches' : view === 'opportunities' ? 'Opportunités' : view === 'companies' ? 'Clients CRM' : view === 'team' ? 'Équipe' : view === 'audit' ? "Journal d'audit" : 'Tableau de bord'}
              </h1>
              <div className="mt-0.5 hidden sm:flex items-center gap-2">
                <p className="text-xs font-medium text-slate-400">Connecté : <span className="text-cyan-300 font-bold">{currentUser.name}</span></p>
                <span className={`orion-mode-dot ${isDbMode ? 'orion-mode-dot-db' : 'orion-mode-dot-demo'}`} title={isDbMode ? 'Mode DB' : 'Mode Demo'} />
              </div>
            </div>
          </div>

          {/* Droite : actions */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {/* Quitter impersonation */}
            {originalUser && (
              <button onClick={handleStopImpersonation}
                className="hidden sm:flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-3 py-2 rounded-xl shadow-lg text-xs font-black transition-all">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" /></svg>
                <span className="hidden lg:inline">Quitter la vue client</span>
              </button>
            )}

            {/* Recherche desktop */}
            <div ref={searchRef} className="relative hidden lg:block">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
              <input type="text" value={globalSearch} onChange={(e) => handleGlobalSearch(e.target.value)}
                onFocus={() => { if (searchResults.length > 0) setShowSearchResults(true); }}
                placeholder="Rechercher un prospect..."
                className="w-48 lg:w-64 pl-9 pr-3 py-2 rounded-xl bg-white/10 border border-slate-200/20 text-sm text-slate-200 placeholder:text-slate-500 outline-none focus:ring-2 focus:ring-cyan-500/30 focus:bg-white/15 transition-all" />
              {showSearchResults && searchResults.length > 0 && (
                <div className="absolute top-full mt-2 right-0 w-72 lg:w-80 bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 overflow-hidden z-50">
                  <div className="p-2 border-b border-slate-100 dark:border-slate-800">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide px-2">{searchResults.length} résultat{searchResults.length > 1 ? 's' : ''}</span>
                  </div>
                  <div className="max-h-72 overflow-y-auto">
                    {searchResults.map(lead => (
                      <button key={lead.id} type="button"
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 border-b border-slate-50 dark:border-slate-800 last:border-0"
                        onClick={() => { setView('leads'); setGlobalSearch(''); setShowSearchResults(false); }}>
                        <div className="w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/40 flex items-center justify-center flex-shrink-0">
                          <span className="text-xs font-black text-indigo-600 dark:text-indigo-400">{(lead.name || '?')[0].toUpperCase()}</span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-bold text-slate-800 dark:text-white truncate">{lead.name}</p>
                          <p className="text-[10px] text-slate-400 truncate">{lead.clientCompanyName || lead.email}</p>
                        </div>
                        <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full flex-shrink-0 ${lead.status === 'won' ? 'bg-emerald-100 text-emerald-700' : lead.status === 'lost' ? 'bg-rose-100 text-rose-700' : 'bg-blue-100 text-blue-700'}`}>{lead.status}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Icône recherche mobile */}
            <button type="button" onClick={() => setMobileSearchOpen(true)}
              className="lg:hidden p-2 rounded-xl text-slate-400 hover:text-white hover:bg-white/10 transition-all"
              aria-label="Rechercher">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
            </button>

            {/* Toggle thème */}
            <button type="button" onClick={() => setIsDark(d => !d)}
              className="p-2 rounded-xl transition-all hover:scale-110 focus:outline-none"
              title={isDark ? 'Mode clair' : 'Mode sombre'} aria-label={isDark ? 'Mode clair' : 'Mode sombre'}>
              {isDark ? (
                <svg className="w-5 h-5 text-amber-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.25a.75.75 0 01.75.75v2.25a.75.75 0 01-1.5 0V3a.75.75 0 01.75-.75zM7.5 12a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM18.894 6.166a.75.75 0 00-1.06-1.06l-1.591 1.59a.75.75 0 101.06 1.061l1.591-1.59zM21.75 12a.75.75 0 01-.75.75h-2.25a.75.75 0 010-1.5H21a.75.75 0 01.75.75zM17.834 18.894a.75.75 0 001.06-1.06l-1.59-1.591a.75.75 0 10-1.061 1.06l1.59 1.591zM12 18a.75.75 0 01.75.75V21a.75.75 0 01-1.5 0v-2.25A.75.75 0 0112 18zM7.166 17.834a.75.75 0 00-1.06 1.06l1.59 1.591a.75.75 0 001.061-1.06l-1.59-1.591zM6 12a.75.75 0 01-.75.75H3a.75.75 0 010-1.5h2.25A.75.75 0 016 12zM6.166 6.166a.75.75 0 00-1.06 1.06l1.59 1.591a.75.75 0 001.061-1.06L6.166 6.166z"/></svg>
              ) : (
                <svg className="w-5 h-5 text-slate-500" fill="currentColor" viewBox="0 0 24 24"><path fillRule="evenodd" d="M9.528 1.718a.75.75 0 01.162.819A8.97 8.97 0 009 6a9 9 0 009 9 8.97 8.97 0 003.463-.69.75.75 0 01.981.98 10.503 10.503 0 01-9.694 6.46c-5.799 0-10.5-4.701-10.5-10.5 0-4.368 2.667-8.112 6.46-9.694a.75.75 0 01.818.162z" clipRule="evenodd"/></svg>
              )}
            </button>

            <NotificationCenter userId={currentUser.id} />

            {/* Avatar / profil */}
            <div onClick={() => { setShowProfileEdit(true); setProfileForm({ email: currentUser.email, password: '', confirmPassword: '', currentPassword: '' }); setProfileMsg(null); }}
              className={`orion-user-chip flex items-center gap-2 p-2 md:p-2.5 rounded-2xl border shadow-sm cursor-pointer hover:opacity-80 transition-opacity ${originalUser ? 'border-indigo-200 ring-2 ring-indigo-500/10' : 'border-slate-200'}`}>
              {isLoading && <div className="hidden sm:flex items-center gap-2 text-[10px] font-bold text-indigo-600 animate-pulse px-2">SYNC...</div>}
              <div className="hidden lg:block text-right">
                <div className="text-sm font-bold text-slate-800">{currentUser.name}</div>
                <div className="text-[10px] text-slate-400 uppercase font-bold tracking-widest leading-none">{(currentUser.role || '').replace('_', ' ')}</div>
              </div>
              <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${currentUser.email}`} className="w-8 h-8 md:w-10 md:h-10 rounded-xl border border-slate-100 bg-slate-50 shadow-sm" alt="Avatar" />
            </div>
          </div>
        </header>

        {view === 'settings' && currentCompany && currentUser && !isSystemUser && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <SettingsView
              user={currentUser}
              company={currentCompany}
              onCompanyUpdate={updateCompanyInState}
              onUserUpdate={(updatedUser) => {
                setCurrentUser(prev => (prev && String(prev.id) === String(updatedUser.id) ? { ...prev, ...updatedUser } : prev));
              }}
            />
          </div>
        )}
        {view === 'analytics' && currentCompany && currentUser && !isSystemUser && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <AnalyticsView user={currentUser} company={currentCompany} />
          </div>
        )}
        {isSystemUser && (
          <div className="flex-1 min-h-0 overflow-y-auto">
            <SuperAdminDashboard activeView={view} onImpersonate={handleImpersonate} currentUser={currentUser} />
          </div>
        )}
        {!isSystemUser && (view === 'dashboard' || view === 'leads' || view === 'tasks' || view === 'planning' || view === 'opportunities') && (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {currentUser.role === UserRole.COMPANY_ADMIN && currentCompany && <CompanyDashboard user={currentUser} company={currentCompany} activeView={view} />}
            {currentUser.role === UserRole.USER && currentCompany && <UserDashboard user={currentUser} company={currentCompany} activeView={view} />}
          </div>
        )}
      </main>
    </div>
    {/* MODAL ÉDITION PROFIL */}
    {showProfileEdit && currentUser && (
      <div className="orion-modal-backdrop fixed inset-0 z-[200] flex items-center justify-center p-4 backdrop-blur-sm" onClick={() => setShowProfileEdit(false)}>
        <div className="orion-modal-panel w-full max-w-sm rounded-[2rem] shadow-2xl p-8 border" onClick={e => e.stopPropagation()}>
          <div className="flex items-center gap-3 mb-6">
            <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${currentUser.email}`} className="w-12 h-12 rounded-xl border border-slate-700 bg-slate-900" alt="Avatar" />
            <div>
              <h3 className="text-lg font-black text-white">{currentUser.name}</h3>
              <p className="text-xs text-slate-400">{(currentUser.role || '').replace('_', ' ')}</p>
            </div>
          </div>
          <form onSubmit={async (e) => {
            e.preventDefault();
            setProfileMsg(null);
            if (!profileForm.currentPassword) { setProfileMsg({ type: 'error', text: 'Mot de passe actuel requis' }); return; }
            if (profileForm.password && profileForm.password !== profileForm.confirmPassword) { setProfileMsg({ type: 'error', text: 'Les mots de passe ne correspondent pas' }); return; }
            setProfileSaving(true);
            try {
              const updated = await api.updateProfile({
                email: profileForm.email !== currentUser.email ? profileForm.email : undefined,
                password: profileForm.password || undefined,
                current_password: profileForm.currentPassword
              });
              setCurrentUser(prev => prev ? { ...prev, ...updated } : prev);
              setProfileMsg({ type: 'success', text: 'Profil mis à jour' });
              setTimeout(() => setShowProfileEdit(false), 1200);
            } catch (err: any) {
              setProfileMsg({ type: 'error', text: err?.message || 'Erreur' });
            } finally {
              setProfileSaving(false);
            }
          }} className="space-y-4">
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1 block">Email</label>
              <input type="email" value={profileForm.email} onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} className="orion-lead-select w-full text-sm px-4 py-2.5 rounded-xl" />
            </div>
            <div>
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1 block">Nouveau mot de passe</label>
              <input type="password" value={profileForm.password} onChange={e => setProfileForm(p => ({ ...p, password: e.target.value }))} placeholder="Laisser vide pour ne pas changer" className="orion-lead-select w-full text-sm px-4 py-2.5 rounded-xl" />
            </div>
            {profileForm.password && (
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1 block">Confirmer</label>
                <input type="password" value={profileForm.confirmPassword} onChange={e => setProfileForm(p => ({ ...p, confirmPassword: e.target.value }))} className="orion-lead-select w-full text-sm px-4 py-2.5 rounded-xl" />
              </div>
            )}
            <div className="pt-2 border-t border-white/10">
              <label className="text-[10px] font-black uppercase tracking-widest text-amber-400 mb-1 block">Mot de passe actuel (requis)</label>
              <input type="password" value={profileForm.currentPassword} onChange={e => setProfileForm(p => ({ ...p, currentPassword: e.target.value }))} className="orion-lead-select w-full text-sm px-4 py-2.5 rounded-xl" />
            </div>
            {profileMsg && (
              <div className={`text-xs font-bold px-3 py-2 rounded-xl ${profileMsg.type === 'success' ? 'text-emerald-400 bg-emerald-500/10' : 'text-rose-400 bg-rose-500/10'}`}>{profileMsg.text}</div>
            )}
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowProfileEdit(false)} className="orion-lead-select flex-1 text-center text-xs font-black py-2.5 rounded-xl">Annuler</button>
              <button type="submit" disabled={profileSaving} className="orion-modal-primary-btn flex-1 text-white text-xs font-black py-2.5 rounded-xl disabled:opacity-50">{profileSaving ? 'Sauvegarde...' : 'Enregistrer'}</button>
            </div>
          </form>
        </div>
      </div>
    )}
    </AppErrorBoundary>
  );
};

export default App;
